// features/workstations/catalyst/hypotheses-section.js
// ═══════════════════════════════════════════════════════════
// Hypotheses section — lists active + retired hypotheses from
// the catalyst_hypotheses table. Active = pre-registered or
// proposed under the current methodology version. Retired =
// pre-prediction retirements from prior versions.
//
// Reads from /api/workstations/catalyst/list-hypotheses
// Styles in styles/catalyst.css
// ═══════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';

const REPO_BLOB_BASE = 'https://github.com/timmai1004-ship-it/shift-prototype/blob/main';

export default function HypothesesSection({ userId }) {
  const [data, setData] = useState({ active: [], retired: [], loading: true, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const qs = userId ? `?user_id=${encodeURIComponent(userId)}` : '';
        const res = await fetch(`/api/workstations/catalyst/list-hypotheses${qs}`, { cache: 'no-store' });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const payload = await res.json();
        if (cancelled) return;
        setData({
          active: payload.active || [],
          retired: payload.retired || [],
          loading: false,
          error: null,
        });
      } catch (err) {
        if (!cancelled) setData((s) => ({ ...s, loading: false, error: err.message }));
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (data.loading) {
    return <div className="cat-section"><div className="cat-loading">Loading hypothesis library…</div></div>;
  }
  if (data.error) {
    return (
      <div className="cat-section">
        <div className="cat-error">
          Could not load hypotheses: {data.error}
          <div className="cat-error-hint">
            If you see a missing-table error, the catalyst SQL migrations
            (catalyst-research-migration.sql + catalyst-v1.1-amendment.sql +
            catalyst-v2.0-migration.sql) need to run in Supabase.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cat-section">
      <div className="cat-block">
        <div className="cat-block-title">Active hypotheses (catalyst-v2.0)</div>
        <div className="cat-block-subtitle">
          Pre-registered + proposed under the current methodology version. The
          hypothesis card content lives in markdown in the repo — click <strong>view card</strong> to
          read the locked claim, falsifier, and binding statements.
        </div>
        {data.active.length === 0 ? (
          <div className="cat-empty">
            No active hypotheses found. Apply <code>catalyst-v2.0-migration.sql</code> in Supabase.
          </div>
        ) : (
          <div className="cat-hypothesis-list">
            {data.active.map((h) => <HypothesisRow key={h.hypothesis_code} hypothesis={h} />)}
          </div>
        )}
      </div>

      {data.retired.length > 0 && (
        <div className="cat-block">
          <div className="cat-block-title">Retired hypotheses</div>
          <div className="cat-block-subtitle">
            Pre-prediction retirements from prior methodology versions (v1.0 / v1.1).
            No predictions were ever locked under any of these — preserved as
            historical artifacts. See <strong>REFRAME_2026-05-07.md</strong> for the v1.x → v2.0 rationale.
          </div>
          <div className="cat-hypothesis-list">
            {data.retired.map((h) => <HypothesisRow key={h.hypothesis_code} hypothesis={h} retired />)}
          </div>
        </div>
      )}
    </div>
  );
}

function HypothesisRow({ hypothesis, retired }) {
  const code = hypothesis.hypothesis_code;
  const status = hypothesis.status || 'unknown';
  const isRoot = !hypothesis.parent_hypothesis_code;
  const docPath = hypothesis.card_doc_path;

  return (
    <div className={`cat-hyp-row${retired ? ' retired' : ''}`}>
      <div className="cat-hyp-row-left">
        <div className="cat-hyp-code">
          {isRoot && <span className="cat-hyp-root-marker">ROOT ◆</span>}
          {code}
        </div>
        <div className="cat-hyp-meta">
          <StatusBadge status={status} />
          <span className="cat-hyp-version">{hypothesis.methodology_version}</span>
          <span className="cat-hyp-vertical">{hypothesis.vertical}</span>
        </div>
      </div>
      <div className="cat-hyp-row-right">
        {docPath && (
          <a
            className="cat-hyp-doc-link"
            href={`${REPO_BLOB_BASE}/${docPath}`}
            target="_blank"
            rel="noreferrer"
          >
            view card ↗
          </a>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const classMap = {
    'pre-registered': 'cat-badge-active',
    'active':         'cat-badge-active',
    'proposed':       'cat-badge-proposed',
    'validated':      'cat-badge-validated',
    'falsified':      'cat-badge-falsified',
    'retired':        'cat-badge-retired',
  };
  const cls = classMap[status] || 'cat-badge-default';
  return <span className={`cat-badge ${cls}`}>{status}</span>;
}
